import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  useTheme,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
  Button
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
  Cell
} from 'recharts';
import RefreshIcon from '@mui/icons-material/Refresh';
import { format } from 'date-fns';
import DashboardTitle from '../../../components/Common/DashboardTitle';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useDomain, DOMAINS } from '../../../contexts/DomainContext';

const lightenHexColor = (hex, factor = 0.3) => {
  if (typeof hex !== 'string' || !/^#([0-9a-fA-F]{6})$/.test(hex)) {
    return hex;
  }
  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) & 0xff;
  let g = (num >> 8) & 0xff;
  let b = num & 0xff;

  r = Math.min(255, Math.round(r + (255 - r) * factor));
  g = Math.min(255, Math.round(g + (255 - g) * factor));
  b = Math.min(255, Math.round(b + (255 - b) * factor));

  const toHex = (value) => value.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};


const MobileKpiChart = ({
  kpiData,
  isLoading,
  onFilterChange,
  filter,
  getChartColors
}) => {
  const theme = useTheme();
  const { domain } = useDomain();
  const isDarkMode = theme.palette.mode === 'dark';

  // 현재 차트의 필터 상태
  const [localFilter, setLocalFilter] = useState({
    categoryCd: kpiData?.categoryCd,
    date: filter?.date || new Date().toISOString().split('T')[0],
    range: filter?.range || 'week'
  });

  // 외부 필터가 변경되면 로컬 필터도 업데이트
  useEffect(() => {
    if (filter) {
      setLocalFilter({
        categoryCd: kpiData?.categoryCd,
        date: filter.date,
        range: filter.range
      });
    }
  }, [filter, kpiData?.categoryCd]);

  // 도메인별 색상 설정
  const getTextColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#f0e6d9' : 'rgba(0, 0, 0, 0.87)';
    }
    return isDarkMode ? '#b3c5e6' : 'rgba(0, 0, 0, 0.87)';
  };

  const getSecondaryTextColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? 'rgba(240, 230, 217, 0.7)' : 'rgba(0, 0, 0, 0.6)';
    }
    return isDarkMode ? 'rgba(179, 197, 230, 0.7)' : 'rgba(0, 0, 0, 0.6)';
  };

  const gridColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';


  const getTooltipBgColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#3d2814' : '#ffffff';
    }
    return isDarkMode ? '#1e3a5f' : '#ffffff';
  };

  const getTooltipBorderColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
    }
    return isDarkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)';
  };

  // 차트 데이터 키 추출
  const dataKeys = useMemo(() => {
    if (!kpiData || !kpiData.chartData || kpiData.chartData.length === 0) {
      return [];
    }

    // 첫 번째 데이터 포인트에서 "name" 키를 제외한 모든 키 추출
    const firstDataPoint = kpiData.chartData[0];
    return Object.keys(firstDataPoint).filter(key => key !== 'name' && !key.startsWith('__') && !key.startsWith('forecast_') && key !== 'isHoliday');
  }, [kpiData]);

  // 범위 변경 핸들러 (버튼 클릭용)
  const handleRangeChange = (range) => {
    const newFilter = {
      ...localFilter,
      categoryCd: kpiData.categoryCd,
      range: range
    };

    setLocalFilter(newFilter);

    // 부모 컴포넌트에 필터 변경 알림 (즉시 데이터 요청)
    if (onFilterChange) {
      onFilterChange(kpiData.kpiIndicatorCd, newFilter, true);
    }
  };

  // 필터 새로고침 핸들러
  const handleRefresh = () => {
    if (onFilterChange) {
      const refreshFilter = {
        ...localFilter,
        categoryCd: kpiData.categoryCd
      };
      onFilterChange(kpiData.kpiIndicatorCd, refreshFilter, true);
    }
  };

  // 차트 타입에 따른 컴포넌트 렌더링
  const renderChart = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!kpiData || !kpiData.chartData || kpiData.chartData.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body1" color={getSecondaryTextColor()}>
            데이터가 없습니다.
          </Typography>
        </Box>
      );
    }

    const chartColors = getChartColors();
    const actualKeys = dataKeys;
    const forecastSeriesMeta = kpiData?.forecastMeta?.series || {};
    const forecastEntries = actualKeys
      .map((key) => {
        const meta = forecastSeriesMeta[key];
        const forecastKey = meta?.forecastKey || `forecast_${key}`;
        const hasForecastValue = Array.isArray(kpiData.chartData) && kpiData.chartData.some(point => typeof point?.[forecastKey] === 'number');
        if (!hasForecastValue) return null;

        let detectedStartIndex = meta?.startIndex;
        if (detectedStartIndex == null) {
          const index = kpiData.chartData.findIndex((point, idx) => {
            const forecastVal = point?.[forecastKey];
            const hasActual = typeof point?.[key] === 'number';
            return typeof forecastVal === 'number' && !hasActual && idx > 0;
          });
          detectedStartIndex = index === -1 ? null : index;
        }

        const lastActualIndex = meta?.lastActualIndex ?? (detectedStartIndex != null ? detectedStartIndex - 1 : kpiData.chartData.length - 1);

        return {
          metricKey: key,
          forecastKey,
          startIndex: detectedStartIndex,
          lastActualIndex
        };
      })
      .filter(Boolean);

    const forecastInfoByKey = forecastEntries.reduce((acc, entry) => {
      acc[entry.metricKey] = entry;
      return acc;
    }, {});

    const hasForecast = forecastEntries.length > 0;
    const earliestStartIndex = hasForecast
      ? forecastEntries.reduce((min, item) => {
          const idx = item.startIndex ?? (item.lastActualIndex != null ? item.lastActualIndex + 1 : null);
          if (idx == null) return min;
          return min == null ? idx : Math.min(min, idx);
        }, null)
      : null;
    const forecastStartLabel = earliestStartIndex != null && earliestStartIndex > -1
      ? kpiData.chartData[earliestStartIndex]?.name
      : null;
    const forecastEndLabel = hasForecast && kpiData.chartData.length > 0
      ? kpiData.chartData[kpiData.chartData.length - 1]?.name
      : null;
    const lastActualIndexGlobal = hasForecast
      ? forecastEntries.reduce((max, entry) => {
          if (entry.lastActualIndex == null) return max;
          return max == null ? entry.lastActualIndex : Math.max(max, entry.lastActualIndex);
        }, null)
      : null;
    const lastActualLabel = lastActualIndexGlobal != null && lastActualIndexGlobal > -1
      ? kpiData.chartData[lastActualIndexGlobal]?.name
      : null;
    const shadingColor = hasForecast
      ? lightenHexColor(chartColors[Math.max(0, actualKeys.indexOf(forecastEntries[0]?.metricKey)) % chartColors.length].solid, 0.5)
      : null;

    const formatTooltipValue = (value, name, props) => {
      const dataKey = props?.dataKey;
      const isForecastKey = typeof dataKey === 'string' && dataKey.startsWith('forecast_');
      const displayName = name || (isForecastKey ? dataKey.replace('forecast_', '') : dataKey);

      if (dataKey === 'isHoliday') {
          return null;
      }
      if (value == null || value === '') {
        return ['-', displayName];
      }
      const numericValue = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(numericValue)) {
        return [value, displayName];
      }
      const unitLabel = kpiData.unit ? ` ${kpiData.unit}` : '';
      return [`${numericValue.toFixed(2)}${unitLabel}`, displayName];
    };

    // 현재 날짜를 기준으로 미래 날짜 데이터 필터링
    const today = new Date();
    const todayString = format(today, 'yyyy-MM-dd');
    
    const normalizedChartData = Array.isArray(kpiData.chartData)
      ? kpiData.chartData
          .filter((entry) => {
            // name이 날짜 형식인 경우 미래 날짜 필터링
            if (entry.name && typeof entry.name === 'string') {
              // YYYY-MM-DD 형식인지 확인
              if (/^\d{4}-\d{2}-\d{2}$/.test(entry.name)) {
                return entry.name <= todayString; // 오늘 이전 또는 오늘까지만 표시
              }
            }
            return true; // 날짜 형식이 아닌 경우 모두 통과
          })
          .map((entry) => {
            const isHolidayValue = !!entry.isHoliday;
            const normalized = { ...entry, isHoliday: isHolidayValue };
            actualKeys.forEach((key) => {
              const value = normalized[key];
              if (value == null || value === '') {
                normalized[key] = null;
              } else {
                const numericValue = Number(value);
                normalized[key] = Number.isFinite(numericValue) ? numericValue : null;
              }
            });
            forecastEntries.forEach(({ forecastKey }) => {
              const value = normalized[forecastKey];
              if (value == null || value === '') {
                normalized[forecastKey] = null;
              } else {
                const numericValue = Number(value);
                normalized[forecastKey] = Number.isFinite(numericValue) ? numericValue : null;
              }
            });
            return normalized;
          })
      : [];


    // targetType에 따른 달성 여부 확인 및 색상 결정 함수
    const getBarColorForValue = (currentValue) => {
      const targetValue = kpiData.targetValue;
      const targetType = kpiData.targetType;

      if (!targetValue || currentValue === null || currentValue === undefined) {
        return chartColors[0]; // 기본 색상
      }

      let isAchieved = false;
      switch (targetType?.toUpperCase()) {
        case 'INCREASE':
          isAchieved = currentValue >= targetValue;
          break;
        case 'DECREASE':
          isAchieved = currentValue <= targetValue;
          break;
        case 'EQUAL':
          const tolerance = targetValue * 0.05; // 5% 허용 오차
          isAchieved = Math.abs(currentValue - targetValue) <= tolerance;
          break;
        default:
          isAchieved = currentValue >= targetValue; // 기본: INCREASE
      }

      return isAchieved ? '#4caf50' : '#f44336'; // 달성: 녹색, 미달성: 빨간색
    };

    // 현재 필터 범위에 따른 X축 라벨 포맷 설정
    const formatXAxis = (value) => {
      if (!value) return '';

      // 날짜 형식(YYYY-MM-DD)인지 확인
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        switch (localFilter.range) {
          case 'day':
            return value.substring(5); // MM-DD 형식으로 표시
          case 'week':
            return value.substring(5); // MM-DD 형식으로 표시
          case 'month':
            return value.substring(8); // DD 형식으로 표시
          default:
            return value;
        }
      }

      // 시간 형식인 경우 (08, 09, 10 등)
      if (localFilter.range === 'day' && /^\d{1,2}$/.test(value)) {
        return `${value}시`;
      }

      return value;
    };

    const formatXAxisWithHoliday = (value, index) => {
      if (!value) return '';
      let formattedValue = formatXAxis(value);
      const entry = normalizedChartData[index];

      if (entry && entry.isHoliday === true) {
        formattedValue += ' 🔴';
      }
      return formattedValue;
    };

    const formatTooltipLabel = (label) => {
        // label은 XAxis의 dataKey="name" 값입니다.

        // label을 name으로 가진 데이터를 찾아 isHoliday 여부를 확인
        const dataPoint = normalizedChartData.find(entry => entry.name === label);

        let formattedLabel = formatXAxis(label); // 기본 X축 포맷 적용

        if (dataPoint && dataPoint.isHoliday) {
            formattedLabel += ' (공휴일)';
        }

        return formattedLabel;
    };

    // 차트 타입에 따라 다른 차트 렌더링
    switch (kpiData.chartType?.toLowerCase()) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={normalizedChartData}
              margin={{ top: 20, right: 20, left: 15, bottom: 15 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="name"
                tick={{ fill: getSecondaryTextColor(), fontSize: 12 }}
                tickFormatter={formatXAxisWithHoliday}
                axisLine={{ stroke: getSecondaryTextColor() }}
                tickLine={{ stroke: getSecondaryTextColor() }}
                angle={-45}
                textAnchor="end"
                height={40}
                />
              <YAxis
                tick={{ fill: getSecondaryTextColor(), fontSize: 12 }}
                tickFormatter={(value) => {
                  if (typeof value === 'number') {
                    if (value >= 1000000) {
                      return `${(value / 1000000).toFixed(1)}M`;
                    } else if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                  }
                  return Number(value).toFixed(0);
                }}
                axisLine={{ stroke: getSecondaryTextColor() }}
                tickLine={{ stroke: getSecondaryTextColor() }}
                label={{
                  value: kpiData.unit,
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: getTextColor(), fontSize: 12, fontWeight: 600 }
                }}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  backdropFilter: 'blur(12px)',
                  color: '#2d3748',
                  borderRadius: 12,
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15), 0 0 0 1px rgba(99, 102, 241, 0.1)',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: 600
                }}
                formatter={(value, name) => {
                  const unitLabel = kpiData.unit ? ` ${kpiData.unit}` : '';
                  return [`${Number(value).toFixed(0)}${unitLabel}`, name];
                }}
                labelFormatter={formatTooltipLabel}
              />

              {dataKeys.map((key, index) => (
                <Bar
                  key={key}
                  dataKey={key}
                  name={key}
                  radius={[4, 4, 0, 0]}
                  strokeWidth={2}
                >
                  {normalizedChartData.map((entry, entryIndex) => {
                    const currentValue = entry[key];
                    const targetValue = kpiData.targetValue;
                    const targetType = kpiData.targetType;

                    let barColor = chartColors[index % chartColors.length].solid; // 기본 색상

                    if (targetValue && currentValue !== null && currentValue !== undefined) {
                      let isAchieved = false;
                      switch (targetType?.toUpperCase()) {
                        case 'INCREASE':
                          isAchieved = currentValue >= targetValue;
                          break;
                        case 'DECREASE':
                          isAchieved = currentValue <= targetValue;
                          break;
                        case 'EQUAL':
                          const tolerance = targetValue * 0.05; // 5% 허용 오차
                          isAchieved = Math.abs(currentValue - targetValue) <= tolerance;
                          break;
                        default:
                          isAchieved = currentValue >= targetValue; // 기본: INCREASE
                      }

                      barColor = isAchieved ? '#23cd5c' : '#f5576c'; // 달성: 모던 그린, 미달성: 모던 레드
                    }

                    return (
                      <Cell
                        key={`cell-${entryIndex}`}
                        fill={barColor}
                        stroke={barColor}
                        style={{
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                        }}
                      />
                    );
                  })}
                </Bar>
              ))}

              {kpiData.targetValue && (
                <ReferenceLine
                  y={kpiData.targetValue}
                  stroke="#ff0000"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  strokeOpacity={0.8}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
      default:
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={normalizedChartData}
              margin={{ top: 4, right: 4, left: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="name"
                tick={{ fill: getSecondaryTextColor(), fontSize: 12 }}
                tickFormatter={formatXAxisWithHoliday}
                axisLine={{ stroke: getSecondaryTextColor() }}
                tickLine={{ stroke: getSecondaryTextColor() }}
                angle={-30}
                textAnchor="end"
                height={40}
                interval={0}
              />
              <YAxis
                tick={{ fill: getSecondaryTextColor(), fontSize: 12 }}
                axisLine={{ stroke: getSecondaryTextColor() }}
                tickLine={{ stroke: getSecondaryTextColor() }}
                tickFormatter={(value) => {
                  if (typeof value === 'number') {
                    if (kpiData.unit === '%') {
                      return Number(value).toFixed(2);
                    }
                    if (value >= 1000000) {
                      return `${(value / 1000000).toFixed(1)}M`;
                    } else if (value >= 1000) {
                      return `${(value / 1000).toFixed(1)}K`;
                    }
                  }
                  return Number(value).toFixed(2);
                }}
                label={{
                  value: kpiData.unit,
                  angle: -90,
                  position: 'insideLeft',
                  style: { fill: getTextColor(), fontSize: 12, fontWeight: 600 }
                }}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: getTooltipBgColor(),
                  borderColor: getTooltipBorderColor(),
                  color: getTextColor(),
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  border: 'none',
                  fontSize: '16px',
                  fontWeight: 600
                }}
                formatter={formatTooltipValue}
                labelFormatter={formatTooltipLabel}
              />
              <Legend wrapperStyle={{ color: getTextColor(), fontSize: '12px', fontWeight: 600 }} />
              {kpiData.targetValue && (
                <ReferenceLine
                  y={kpiData.targetValue}
                  stroke="#ff5722"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{
                    value: `목표: ${Number(kpiData.targetValue).toFixed(2)}${kpiData.unit || ''}`,
                    fill: '#ff5722',
                    position: 'insideTopRight',
                    fontSize: 11,
                    fontWeight: 500
                  }}
                />
              )}

              {actualKeys.map((key, index) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={chartColors[index % chartColors.length].solid}
                  strokeWidth={4}
                  dot={{
                    r: 4,
                    fill: chartColors[index % chartColors.length].solid,
                    stroke: '#ffffff',
                    strokeWidth: 2
                  }}
                  activeDot={{
                    r: 8,
                    fill: chartColors[index % chartColors.length].solid,
                    stroke: '#ffffff',
                    strokeWidth: 3,
                    style: {
                      filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                    }
                  }}
                  name={key}
                  style={{
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                  }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <Box sx={{
      color: getTextColor(),
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ display: 'flex',flexDirection: 'column', alignItems: 'center', mb: 0.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1, width: '100%' }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              fontSize: '16px',
              color: getTextColor(),
              mb: 1.5
            }}
          >
            {kpiData?.kpiTitle.split('의')[1] || 'KPI 지표'}
          </Typography>
          <Box>
            <Tooltip title="공휴일" arrow placement="top">
              <Chip
                label="🔴 공휴일"
                size="small"
                sx={{
                  bgcolor: 'rgba(244, 67, 54, 0.1)',
                  color: '#f44336',
                  fontWeight: 500,
                  fontSize: { xs: '9px', sm: '10px' },
                  height: { xs: '20px', sm: '24px' },
                  '& .MuiChip-label': {
                    px: { xs: 0.8, sm: 1 }
                  },
                  border: '1px solid rgba(244, 67, 54, 0.3)',
                  cursor: 'help',
                  mr: 1
                }}
              />
            </Tooltip>
            {kpiData?.categoryNm && (
              <Chip
                label={kpiData.categoryNm}
                size="medium"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: { xs: '0.7rem', sm: '0.8rem' },
                  height: { xs: '24px', sm: '28px' },
                  '& .MuiChip-label': {
                    px: { xs: 1.5, sm: 2 }
                  },
                  boxShadow: '0 3px 12px rgba(102, 126, 234, 0.4)',
                  border: 'none',
                  display: { xs: kpiData.categoryNm.length > 10 ? 'none' : 'inline-flex', sm: 'inline-flex' }
                }}
              />
            )}
          </Box>
        </Box>

        {/* 반응형 필터 컨트롤 - 심플한 버튼 그룹 */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 2,
          mb: 1.5
        }}>
            <Button
              onClick={() => handleRangeChange('day')}
              variant="text"
              size="small"
              sx={{
                minWidth: '60px',
                fontSize: '12px',
                fontWeight: localFilter.range === 'day' ? 600 : 400,
                color: localFilter.range === 'day' ? getTextColor() : getSecondaryTextColor(),
                backgroundColor: localFilter.range === 'day' ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                borderRadius: '5px',
                textTransform: 'none',
                py: 0.25,
                px: 1.5,
                minHeight: '28px',
                width: '100%'
              }}
            >
              일간
            </Button>
            <Button
              onClick={() => handleRangeChange('week')}
              variant="text"
              size="small"
              sx={{
                minWidth: '60px',
                fontSize: '12px',
                fontWeight: localFilter.range === 'week' ? 600 : 400,
                color: localFilter.range === 'week' ? getTextColor() : getSecondaryTextColor(),
                backgroundColor: localFilter.range === 'week' ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                borderRadius: '5px',
                textTransform: 'none',
                py: 0.25,
                px: 1.5,
                minHeight: '28px',
                width: '100%'
              }}
            >
              주간
            </Button>
            <Button
              onClick={() => handleRangeChange('month')}
              variant="text"
              size="small"
              sx={{
                minWidth: '60px',
                fontSize: '12px',
                fontWeight: localFilter.range === 'month' ? 600 : 400,
                color: localFilter.range === 'month' ? getTextColor() : getSecondaryTextColor(),
                backgroundColor: localFilter.range === 'month' ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                borderRadius: '5px',
                textTransform: 'none',
                py: 0.25,
                px: 1.5,
                minHeight: '28px',
                width: '100%'
              }}
            >
              월간
            </Button>
        </Box>
      </Box>
      <Box sx={{
        flex: 1,
        width: '100%',
        minHeight: { xs: 120, md: 140 },
        height: '100%',
        position: 'relative'
      }}>
        {renderChart()}
        {kpiData?.targetValue && (
          <Tooltip
            title={`${kpiData?.kpiTitle || 'KPI'} 목표치`}
            arrow
            placement="left"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  backdropFilter: 'blur(20px)',
                  color: '#2d3748',
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.025em',
                  borderRadius: '12px',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04), 0 0 0 2px rgba(147, 51, 234, 0.2)',
                  border: 'none',
                  padding: '12px 16px',
                  minWidth: 'auto',
                  textAlign: 'center'
                }
              },
              arrow: {
                sx: {
                  color: 'rgba(255, 255, 255, 0.98)',
                  filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))',
                  '&::before': {
                    backgroundColor: 'rgba(255, 255, 255, 0.98)',
                    border: '1px solid rgba(147, 51, 234, 0.15)'
                  }
                }
              }
            }}
          >
            <Box sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 1001,
              backgroundColor: '#ff5722',
              color: 'white',
              padding: '6px 10px',
              borderRadius: '16px',
              fontSize: '12px',
              fontWeight: 700,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#e64a19'
              }
            }}>
              목표: {Number(kpiData.targetValue).toFixed(0)}{kpiData.unit || ''}
            </Box>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default MobileKpiChart;
